import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../hooks/useUser";

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { adminLogin, loading, error } = useUser();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminLogin(username, password, rememberMe);
      console.log("Login successful");
      navigate("/dashboard");
    } catch (err) {
      console.error("Login failed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background images - hidden on small screens */}
      <img
        src="/images/login_bg_top_right.svg"
        alt=""
        className="hidden md:block absolute top-0 right-0 w-48 md:w-64 lg:w-80 pointer-events-none -z-1"
        onError={(e) => {
          e.currentTarget.src =
            "https://placehold.co/320x320/ffffff/ffffff?text=.";
        }}
      />
      <img
        src="/images/login_bg_bottom_right.svg"
        alt=""
        className="hidden md:block absolute bottom-0 right-0 w-56 md:w-72 lg:w-96 pointer-events-none -z-1"
        onError={(e) => {
          e.currentTarget.src =
            "https://placehold.co/384x384/ffffff/ffffff?text=.";
        }}
      />

      {/* Central Login Card - different layout for mobile vs desktop */}
      <div className="w-full max-w-4xl">
        {/* Desktop layout with background image */}
        <div
          className="hidden md:flex relative w-full h-[700px] bg-contain bg-no-repeat bg-center"
          style={{ backgroundImage: "url('/images/login_bg_middle.svg')" }}
        >
          {/* Spacer to push content to the right */}
          <div className="w-1/2"></div>
          {/* Form content area */}
          <div className="w-1/2 flex items-center justify-center pr-20 scale-[0.8]">
            <div className="w-full max-w-sm">
              <div className="text-center mb-10">
                <h1
                  className="text-5xl font-bold bg-gradient-to-r from-[#DF4242] to-[#3C0B0B] text-transparent bg-clip-text mb-4"
                  style={{ fontFamily: "serif" }}
                >
                  Moving Rolls
                </h1>
                <p className="text-xl font-semibold text-black">ADMIN PANEL</p>
              </div>
              <form onSubmit={handleLogin} className="w-full">
                {/* Username Input */}
                <div className="mb-6 relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <img
                      src="/logos/phone_call_telephone.svg"
                      alt="username icon"
                      className="h-6 w-6"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                  <input
                    type="text"
                    id="username"
                    className="w-full pl-14 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#3C0B0B] transition-colors"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Username"
                    required
                    disabled={loading}
                  />
                </div>
                {/* Password Input */}
                <div className="mb-6 relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <img
                      src="/logos/password_lock.svg"
                      alt="password icon"
                      className="h-6 w-6"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    className="w-full pl-14 pr-12 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#3C0B0B] transition-colors"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onMouseDown={() => setShowPassword(true)}
                    onMouseUp={() => setShowPassword(false)}
                    onMouseLeave={() => setShowPassword(false)}
                    onTouchStart={() => setShowPassword(true)}
                    onTouchEnd={() => setShowPassword(false)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-opacity duration-200 active:opacity-50 focus:outline-none"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    <img
                      src={"/logos/closed_eye.svg"}
                      alt="Toggle password visibility"
                      className="h-6 w-6"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </button>
                </div>
                {/* Remember Me Custom Checkbox */}
                <div className="flex justify-start items-center mb-8">
                  <label
                    htmlFor="rememberMe"
                    className="flex items-center cursor-pointer"
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="rememberMe"
                        className="sr-only peer"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        disabled={loading}
                      />
                      <div className="w-6 h-6 bg-gray-200 rounded-md peer-checked:bg-[#3C0B0B] transition-colors flex items-center justify-center">
                        <svg
                          className={`w-4 h-4 text-white ${
                            rememberMe ? "block" : "hidden"
                          }`}
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <span className="ml-2 text-gray-800 font-medium">
                      Remember me
                    </span>
                  </label>
                </div>
                {/* Error Message from API */}
                {error && (
                  <div className="mb-4 text-red-600 text-sm text-center bg-red-100 p-3 rounded-lg">
                    {error}
                  </div>
                )}
                {/* Login Button */}
                <button
                  type="submit"
                  style={{ backgroundColor: "#3C0B0B" }}
                  className={`w-full font-semibold py-3 px-4 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#3C0B0B] focus:ring-opacity-50 transition-all duration-300 ease-in-out flex items-center justify-center
                                  ${
                                    loading
                                      ? "bg-gray-400 cursor-not-allowed"
                                      : "hover:bg-opacity-90 hover:shadow-lg"
                                  }`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    "Login"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Mobile layout with simple card */}
        <div className="md:hidden bg-white rounded-xl shadow-lg p-6 w-full max-w-sm mx-auto">
          <div className="text-center mb-8">
            <h1
              className="text-4xl font-bold bg-gradient-to-r from-[#DF4242] to-[#3C0B0B] text-transparent bg-clip-text mb-3"
              style={{ fontFamily: "serif" }}
            >
              Moving Rolls
            </h1>
            <p className="text-lg font-semibold text-black">ADMIN PANEL</p>
          </div>
          <form onSubmit={handleLogin} className="w-full">
            {/* Username Input */}
            <div className="mb-5 relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <img
                  src="/logos/phone_call_telephone.svg"
                  alt="username icon"
                  className="h-5 w-5"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
              <input
                type="text"
                id="username"
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#3C0B0B] transition-colors"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                required
                disabled={loading}
              />
            </div>
            {/* Password Input */}
            <div className="mb-5 relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <img
                  src="/logos/password_lock.svg"
                  alt="password icon"
                  className="h-5 w-5"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                className="w-full pl-12 pr-10 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#3C0B0B] transition-colors"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                disabled={loading}
              />
              <button
                type="button"
                onMouseDown={() => setShowPassword(true)}
                onMouseUp={() => setShowPassword(false)}
                onMouseLeave={() => setShowPassword(false)}
                onTouchStart={() => setShowPassword(true)}
                onTouchEnd={() => setShowPassword(false)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-opacity duration-200 active:opacity-50 focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <img
                  src={"/logos/closed_eye.svg"}
                  alt="Toggle password visibility"
                  className="h-5 w-5"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </button>
            </div>
            {/* Remember Me Custom Checkbox */}
            <div className="flex justify-start items-center mb-6">
              <label
                htmlFor="rememberMe"
                className="flex items-center cursor-pointer"
              >
                <div className="relative">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    className="sr-only peer"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                  />
                  <div className="w-5 h-5 bg-gray-200 rounded-md peer-checked:bg-[#3C0B0B] transition-colors flex items-center justify-center">
                    <svg
                      className={`w-3 h-3 text-white ${
                        rememberMe ? "block" : "hidden"
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                </div>
                <span className="ml-2 text-gray-800 font-medium text-sm">
                  Remember me
                </span>
              </label>
            </div>
            {/* Error Message from API */}
            {error && (
              <div className="mb-4 text-red-600 text-sm text-center bg-red-100 p-3 rounded-lg">
                {error}
              </div>
            )}
            {/* Login Button */}
            <button
              type="submit"
              style={{ backgroundColor: "#3C0B0B" }}
              className={`w-full font-semibold py-3 px-4 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#3C0B0B] focus:ring-opacity-50 transition-all duration-300 ease-in-out flex items-center justify-center
                                ${
                                  loading
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "hover:bg-opacity-90 hover:shadow-lg"
                                }`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
